import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Save, X, Tag, ShieldCheck, DollarSign, Plus } from "lucide-react";
import { api, ApiClientError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";
import { DataTable, type Column } from "../components/DataTable";
import type { ServiceProgram, GrcPricingRow } from "@sbd-tcic/shared-types";

interface ServiceProgramUpdateBody {
  internalDescription?: string;
  externalDescription?: string;
  details?: string;
}

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ServicePrograms() {
  const { can } = useAuth();
  const { toast } = useToast();
  const admin = can("service_programs.admin");

  const [programs, setPrograms] = useState<ServiceProgram[]>([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState<ServiceProgramUpdateBody>({});

  const [pricing, setPricing] = useState<GrcPricingRow[]>([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [priceForm, setPriceForm] = useState({ category: "", price: "" });

  const [newModel, setNewModel] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [addBusy, setAddBusy] = useState(false);

  const loadPrograms = useCallback(async () => {
    setProgramsLoading(true);
    try {
      const data = await api.get<ServiceProgram[]>("/service-programs");
      setPrograms(data);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load service programs.", "error");
    } finally {
      setProgramsLoading(false);
    }
  }, [toast]);

  const loadPricing = useCallback(async () => {
    setPricingLoading(true);
    try {
      const data = await api.get<GrcPricingRow[]>("/grc-pricing");
      setPricing(data);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load GRC pricing.", "error");
    } finally {
      setPricingLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadPrograms();
    void loadPricing();
  }, [loadPrograms, loadPricing]);

  const selected = useMemo(() => programs.find((p) => p.id === selectedId) ?? null, [programs, selectedId]);

  function openProgram(program: ServiceProgram) {
    setSelectedId(program.id);
    setForm({ internalDescription: program.internalDescription, externalDescription: program.externalDescription, details: program.details });
  }

  function closeProgram() {
    setSelectedId(null);
    setForm({});
  }

  async function saveProgram() {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post<ServiceProgram>(`/service-programs/${selected.id}`, form);
      toast(`${selected.name} updated.`, "success");
      await loadPrograms();
      closeProgram();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to update this program.", "error");
    } finally {
      setBusy(false);
    }
  }

  function startEditPrice(row: GrcPricingRow) {
    setEditingModel(row.modelNumber);
    setPriceForm({ category: row.category, price: String(row.price) });
  }

  async function savePrice(modelNumber: string) {
    const price = Number(priceForm.price);
    if (!priceForm.category.trim() || !Number.isFinite(price) || price < 0) {
      toast("A category and a non-negative price are required.", "error");
      return;
    }
    setBusy(true);
    try {
      await api.post<GrcPricingRow>(`/grc-pricing/${modelNumber}`, { category: priceForm.category.trim(), price });
      toast(`Pricing for ${modelNumber} updated.`, "success");
      setEditingModel(null);
      await loadPricing();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to update pricing.", "error");
    } finally {
      setBusy(false);
    }
  }

  async function addOrUpdatePrice() {
    const price = Number(newPrice);
    if (!newModel.trim() || !newCategory.trim() || !Number.isFinite(price) || price < 0) {
      toast("A model number, category, and non-negative price are required.", "error");
      return;
    }
    setAddBusy(true);
    try {
      await api.post<GrcPricingRow>(`/grc-pricing/${newModel.trim()}`, { category: newCategory.trim(), price });
      toast(`Pricing for ${newModel.trim().toUpperCase()} saved.`, "success");
      setNewModel("");
      setNewCategory("");
      setNewPrice("");
      await loadPricing();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to save pricing.", "error");
    } finally {
      setAddBusy(false);
    }
  }

  const pricingColumns: Column<GrcPricingRow>[] = [
    { key: "modelNumber", header: "Model #", render: (r) => <span className="font-bold text-brand-blue">{r.modelNumber}</span> },
    {
      key: "category",
      header: "Category",
      render: (r) =>
        editingModel === r.modelNumber ? (
          <input className="input" value={priceForm.category} onChange={(e) => setPriceForm((f) => ({ ...f, category: e.target.value }))} />
        ) : (
          r.category
        ),
    },
    {
      key: "price",
      header: "Price",
      render: (r) =>
        editingModel === r.modelNumber ? (
          <input
            type="number"
            min={0}
            step={0.01}
            className="input w-28"
            value={priceForm.price}
            onChange={(e) => setPriceForm((f) => ({ ...f, price: e.target.value }))}
          />
        ) : (
          money(r.price)
        ),
    },
    {
      key: "isPlaceholder",
      header: "Status",
      render: (r) =>
        r.isPlaceholder ? (
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-status-orange-bg text-status-orange">
            Placeholder
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-status-green-bg text-status-green">Confirmed</span>
        ),
    },
    ...(admin
      ? [
          {
            key: "actions",
            header: "",
            render: (r: GrcPricingRow) =>
              editingModel === r.modelNumber ? (
                <div className="flex items-center gap-1.5">
                  <button className="btn-primary !px-2.5 !py-1.5" disabled={busy} onClick={() => void savePrice(r.modelNumber)}>
                    <Save size={14} />
                  </button>
                  <button className="btn-outline !px-2.5 !py-1.5" disabled={busy} onClick={() => setEditingModel(null)}>
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button className="btn-outline !px-2.5 !py-1.5" onClick={() => startEditPrice(r)}>
                  <Pencil size={14} />
                </button>
              ),
          } as Column<GrcPricingRow>,
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-6">
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-ink-muted uppercase tracking-wide">Program Catalog</h2>
        </div>
        {programsLoading ? (
          <div className="text-ink-muted text-sm py-10 text-center">Loading service programs…</div>
        ) : programs.length === 0 ? (
          <div className="card p-8 text-center text-sm text-ink-muted">No service programs found.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {programs.map((p) => (
              <button key={p.id} onClick={() => openProgram(p)} className="card p-4 text-left hover:border-brand-blue transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-sm">{p.name}</span>
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold bg-surface text-ink-muted">
                    <Tag size={10} /> {p.category}
                  </span>
                </div>
                <p className="text-xs text-ink-muted line-clamp-3">{p.internalDescription}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="card p-5">
        <div className="flex items-center gap-2 mb-3.5">
          <DollarSign size={16} className="text-ink-muted" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted">GRC Program Pricing</h2>
        </div>
        {pricingLoading ? (
          <div className="text-ink-muted text-sm py-10 text-center">Loading GRC pricing…</div>
        ) : (
          <DataTable columns={pricingColumns} rows={pricing} rowKey={(r) => r.modelNumber} emptyMessage="No GRC pricing rows found." />
        )}

        {admin && (
          <div className="border-t border-border mt-4 pt-4">
            <div className="text-xs font-bold uppercase tracking-wide text-ink-muted mb-2">Add / Update Model Pricing</div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <input className="input" placeholder="Model #" value={newModel} onChange={(e) => setNewModel(e.target.value)} />
              <input className="input" placeholder="Category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
              <input type="number" min={0} step={0.01} className="input" placeholder="Price" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} />
              <button className="btn-primary justify-center" disabled={addBusy || !newModel.trim() || !newCategory.trim() || !newPrice.trim()} onClick={() => void addOrUpdatePrice()}>
                <Plus size={15} /> {addBusy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </section>

      {selected && (
        <div className="fixed inset-0 z-[100] flex justify-end bg-black/40" onClick={closeProgram}>
          <div className="w-full max-w-lg h-full bg-card overflow-y-auto shadow-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10">
              <h2 className="text-base font-extrabold">{selected.name}</h2>
              <button aria-label="Close" className="text-ink-muted hover:text-ink" onClick={closeProgram}>
                <X size={18} />
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4">
              <div className="flex items-center gap-2 text-xs text-ink-muted">
                <Tag size={12} /> Category: <span className="font-semibold text-ink">{selected.category}</span>
              </div>

              <div>
                <label className="field-label">Internal Description</label>
                {admin ? (
                  <textarea
                    className="input min-h-24"
                    value={form.internalDescription ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, internalDescription: e.target.value }))}
                  />
                ) : (
                  <p className="text-sm">{selected.internalDescription}</p>
                )}
              </div>

              <div>
                <label className="field-label">External (Customer-Facing) Description</label>
                {admin ? (
                  <textarea
                    className="input min-h-24"
                    value={form.externalDescription ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, externalDescription: e.target.value }))}
                  />
                ) : (
                  <p className="text-sm">{selected.externalDescription}</p>
                )}
              </div>

              <div>
                <label className="field-label">Details</label>
                {admin ? (
                  <textarea className="input min-h-32" value={form.details ?? ""} onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))} />
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{selected.details}</p>
                )}
              </div>

              {admin ? (
                <button className="btn-primary w-full justify-center" disabled={busy} onClick={() => void saveProgram()}>
                  <Save size={15} /> {busy ? "Saving…" : "Save Changes"}
                </button>
              ) : (
                <div className="flex items-center gap-1.5 text-xs text-ink-muted">
                  <ShieldCheck size={13} /> Read-only — service programs administration is required to edit.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
